/**
 * Строка сводного списка — один отчёт.
 *
 * Читается как предложение: чей отчёт, за какой период, что с кабинетом,
 * когда собран. Корпус тот же, что у строки хранилища, — `SplitPanel`:
 * строки склеиваются швом в одну группу, а не рассыпаются карточками.
 *
 * Вердикт стоит рядом с баллом и набран в его тон. Балл без вердикта
 * требует знать шкалу («34 — это много или мало?»), вердикт без балла не
 * даёт сравнить два проекта между собой; вместе они читаются с одного
 * взгляда.
 *
 * Имя проекта показывается не всегда: в режиме группировки его уже назвал
 * заголовок группы, и повторять его в каждой строке — значит трижды
 * написать одно и то же на экран.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {ChipMUI} from 'ui/components/atoms/ChipMUI';
import {Icon} from 'ui/components/atoms/Icon';
import {ROW_REFLOW_PX, Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {Metric} from '../../ExecutiveReport/components/Metric';
import {SplitPanel} from '../../ExecutiveReport/components/SplitCard';
import {SCORE_ZONE_TONE, SCORE_ZONE_VERDICT, paletteTone} from '../../ExecutiveReport/content';
import {MEASURE, reportPeriodTitle} from '../../ExecutiveReport/helpers';
import {scoreTone, statusMeta} from '../../ReportsArchive/helpers';
import {generatedMoment} from '../helpers';
import type {OverviewReport} from '../types';

// NBSP в JSX-тексте запрещён линтером — типографированные подписи держим
// строками.
const SCORE_LABEL = 'Оценка';
/** Отчёт, который ещё не открывали. */
const NEW_LABEL = 'Новый';
/** Знаменатель шкалы: «34» само по себе не говорит, из скольких. */
const SCORE_MAX = '/100';

/**
 * Ширины колонок правой части строки.
 *
 * Заданы жёстко и держатся, даже когда содержимого нет: у отчёта без
 * балла ячейка остаётся пустой, а не схлопывается. Иначе соседние строки
 * разъезжаются по горизонтали — вердикт одной встаёт под баллом другой, и
 * список перестаёт просматриваться столбиком.
 *
 * Значения сняты по самому длинному содержимому: «Запланирован» в пилюле,
 * «Кабинет требует срочного вмешательства» в вердикте.
 */
const SCORE_COLUMN = spacingPx('xl') * 4;
const VERDICT_COLUMN = spacingPx('xl') * 8;
const STATUS_COLUMN = spacingPx('xl') * 5;
const CHEVRON_COLUMN = spacingPx('l');

/** Базис левой колонки: имя проекта в одну-две строки, а не по слогам. */
const IDENTITY_COLUMN = spacingPx('xl') * 7;

export interface OverviewRowProps {
  report: OverviewReport;
  /** Показывать имя проекта. В группе его называет заголовок группы. */
  showCompany?: boolean;
  /** Открыть отчёт. Не передан — строка остаётся некликабельной. */
  onOpen?: (id: string) => void;
}

const Company = styled(Box)({
  ...text('label.l'),
  color: surfaceVar('onSurface'),
});

const Period = styled(Box)({
  ...text('label.l'),
  color: surfaceVar('onSurface'),
});

/** Период под именем проекта — подпись, а не заголовок. */
const PeriodSub = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
});

const Moment = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
});

const Verdict = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: 'good' | 'alert' | 'bad'}>(({theme, $tone}) => ({
  ...text('body.m'),
  color: theme.palette[$tone].main,
}));

/**
 * Колонка таблицы. Держит свою ширину — и когда пуста, и когда экрана мало.
 *
 * Ни одна из четырёх не сжимается намеренно: стоит разрешить сжатие хотя бы
 * одной, и суммарная ширина правой части начинает зависеть от текста в
 * строке — колонки соседних строк встают на разные вертикали. Место при
 * нехватке уступает левая колонка, у неё для этого есть переносы.
 */
const Cell = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$width: number}>(({$width}) => ({
  flex: `0 0 ${$width}px`,
  minWidth: 0,
  // Длинное слово («вмешательства») иначе не даёт ячейке сжаться ниже своей
  // ширины, и у строк с разным текстом колонки встают на разные вертикали —
  // ровно та табличность, ради которой всё и затевалось.
  overflowWrap: 'anywhere',
  // Ниже порога ряд переворачивается в столбец, и та же величина начинает
  // работать ВЫСОТОЙ ячейки: пустая колонка балла превращалась в пустую
  // полосу в 128 пикселей, а строка вырастала вчетверо. В столбце колонок
  // нет — есть содержимое, и место оно занимает своё.
  [`@media (max-width: ${ROW_REFLOW_PX.lg}px)`]: {
    flex: '0 0 auto',
  },
}));

/**
 * Левая колонка — проект, период и момент выгрузки. Тоже уступает место,
 * но неохотно: базис держит её шире вердикта, пока ширины хватает обоим.
 */
const Identity = styled(Box)({
  flex: `1 1 ${IDENTITY_COLUMN}px`,
  minWidth: 0,
  // См. `Cell`: длинное имя проекта («Стоматология») держало бы колонку
  // шире, чем у соседней строки.
  overflowWrap: 'anywhere',
  [`@media (max-width: ${ROW_REFLOW_PX.lg}px)`]: {
    flex: '0 0 auto',
    width: '100%',
  },
});

const Note = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
  maxWidth: MEASURE,
});

export function OverviewRow({report, showCompany = true, onOpen}: OverviewRowProps) {
  const status = statusMeta(report.status);
  const period = reportPeriodTitle(report.periodFrom, report.periodTo);
  const moment = generatedMoment(report.generatedAt);
  const clickable = report.status === 'ready' && onOpen != null;

  return (
    <SplitPanel
      onClick={clickable ? () => onOpen(report.id) : undefined}
      ariaLabel={
        clickable ? `Открыть отчёт: ${report.companyName}, ${period}` : undefined
      }
    >
      <Row gap='m' alignY='center' justify='between' reflowAt='lg'>
        <Identity>
          <Stack gap='xs'>
          <Row gap='s' alignY='center' wrap='wrap'>
            {showCompany ? <Company>{report.companyName}</Company> : <Period>{period}</Period>}
            {/* Бейдж, а не точка: точка требует знать легенду («что значит
                синий кружок?»), слово объясняет себя само. */}
              {report.unread === true && (
              <ChipMUI mode='badge' color='primary' variant='soft' size='tiny'>
                {NEW_LABEL}
              </ChipMUI>
            )}
          </Row>
          {/* Под заголовком — то, чего в нём не осталось: период, когда
              заголовок занят проектом, и момент выгрузки. */}
          <Row gap='xs' alignY='baseline' wrap='wrap'>
            {showCompany && <PeriodSub>{period}</PeriodSub>}
            {moment != null && <Moment>{`${status.dateLabel} ${moment}`}</Moment>}
          </Row>
          </Stack>
        </Identity>

        {/* Правая часть — таблица без линий: четыре колонки, одинаковые во
            всех строках. Ячейки не переносятся — перенос ломает те самые
            вертикали, ради которых колонки заведены. Когда ширины не
            хватает, место уступает вердикт: он единственный здесь умеет
            переноситься по словам, а число и пилюля от сжатия только
            потеряли бы содержимое. */}
        <Row gap='l' alignY='center' wrap='nowrap' width='hug'>
          <Cell $width={SCORE_COLUMN}>
            {report.score != null && (
              <Metric
                size='m'
                label={SCORE_LABEL}
                value={report.score}
                unit={SCORE_MAX}
                tone={scoreTone(report.score)}
              />
            )}
          </Cell>

          {/* Вердикт держится на полосе шкалы, а не на самом балле: границы
              полос знает сервер, и пересчитывать их здесь значило бы завести
              вторую шкалу. Полосы нет — колонка остаётся пустой. */}
          <Cell $width={VERDICT_COLUMN}>
            {report.scoreZone != null && (
              <Verdict $tone={paletteTone(SCORE_ZONE_TONE[report.scoreZone])}>
                {SCORE_ZONE_VERDICT[report.scoreZone]}
              </Verdict>
            )}
          </Cell>

          <Cell $width={STATUS_COLUMN}>
            <ChipMUI mode='badge' color={status.tone} variant='soft' size='s' leftIcon={status.icon}>
              {report.statusLabel ?? status.label}
            </ChipMUI>
          </Cell>

          <Cell $width={CHEVRON_COLUMN}>
            {clickable && <Icon name='chevron_right' size='s' color='on-surface-muted' />}
          </Cell>
        </Row>
      </Row>

      {report.note != null && <Note>{report.note}</Note>}
    </SplitPanel>
  );
}
